import { useNavigate } from 'react-router'
import clsx from 'clsx'
import { AspectRatio, Table, Image } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { getRankSuffix } from '../../common/helpers'
import { useCompetitionId } from '../../common/hooks'
import { Typography } from '../../common/typography'
import { PlayerRank, TeamRank } from '../../ranks/api'
import classes from './Ranks.module.css'

export type TeamRanks = { type: 'team'; data: TeamRank[] }
export type PlayerRanks = { type: 'player'; data: PlayerRank[] }

export default function RanksComponent({
  ranks,
  active,
  subranks,
}: {
  ranks: TeamRanks | PlayerRanks
  active?: string
  subranks: boolean
}) {
  const navigate = useNavigate()
  const competitionId = useCompetitionId()
  const isMobile = useMediaQuery('(max-width: 800px)')
  const short = active === 'short'
  const full = active === 'full' || !active
  const shouldStickFirstColumn = isMobile && active === 'full'

  const handleRowClick = (id: number) => {
    if (ranks.type === 'team') {
      navigate(`/competitions/${competitionId}/teams/${id}/overview`)
    } else {
      navigate(`/competitions/${competitionId}/players/${id}`)
    }
  }

  const rows = ranks.data.map((item) => {
    const rank = subranks
      ? ranks.type === 'team'
        ? (item as TeamRank).division_rank
        : (item as PlayerRank).team_rank
      : item.rank

    const id =
      ranks.type === 'team'
        ? (item as TeamRank).team_id
        : (item as PlayerRank).player_id
    const name =
      ranks.type === 'team'
        ? (item as TeamRank).team_name
        : (item as PlayerRank).player_name
    const logo =
      ranks.type === 'team'
        ? (item as TeamRank).team_logo.thumbnail
        : (item as PlayerRank).player_photo?.[isMobile ? 'thumbnail' : 'main']
    return (
      <Table.Tr key={id} onClick={() => handleRowClick(id)}>
        <Table.Td>
          <Typography lh="150" ta="center">
            {rank}
            <sup>{getRankSuffix(rank)}</sup>
          </Typography>
        </Table.Td>
        <Table.Td>
          <div className={classes.logos}>
            {ranks.type === 'player' && (
              <AspectRatio ratio={1 / 1}>
                <Image
                  src={(item as PlayerRank).team_logo.thumbnail}
                  alt={(item as PlayerRank).team_name}
                  className={clsx(classes.logo, {
                    [classes.logoMobile]: isMobile,
                  })}
                  fit="scale-down"
                />
              </AspectRatio>
            )}
            <AspectRatio ratio={1 / 1}>
              <Image
                src={logo}
                alt={name}
                className={clsx(
                  classes.logo,
                  { [classes.logoMobile]: isMobile },
                  {
                    [classes.playerPhoto]: ranks.type === 'player',
                  }
                )}
                fit="scale-down"
              />
            </AspectRatio>
          </div>
        </Table.Td>
        <Table.Td className={classes.itemName}>
          <Typography lh="150">{name}</Typography>
        </Table.Td>
        <Table.Td>
          <Typography ff="JetBrains Mono" lh="140" ta="center">
            {item.rating.toFixed(2)}
          </Typography>
        </Table.Td>
        {(full || short) && (
          <Table.Td>
            <Typography ff="JetBrains Mono" lh="140" ta="center">
              {item.points.toFixed(0)}
            </Typography>
          </Table.Td>
        )}
        {full && (
          <>
            <Table.Td>
              <Typography ff="JetBrains Mono" lh="140" ta="center">
                {item.month_points.toFixed(0)}
              </Typography>
            </Table.Td>
            <Table.Td>
              <Typography ff="JetBrains Mono" lh="140" ta="center">
                {item.round_points.toFixed(0)}
              </Typography>
            </Table.Td>
            <Table.Td>
              <Typography ff="JetBrains Mono" lh="140" ta="center">
                {item.bonus_points.toFixed(0)}
              </Typography>
            </Table.Td>
          </>
        )}
      </Table.Tr>
    )
  })

  return (
    <div className={classes.tableContainer}>
      <Table.ScrollContainer minWidth={300}>
        <Table
          classNames={{
            table: clsx(
              classes.table,
              shouldStickFirstColumn && classes.stickyFirstColumn
            ),
            tr: classes.tr,
            th: classes.th,
            td: classes.td,
          }}
          verticalSpacing="xs"
          withRowBorders={false}
        >
          <Table.Thead>
            <Table.Tr className={classes.headerRow}>
              <Table.Th>Pos</Table.Th>
              <Table.Th></Table.Th>
              <Table.Th style={{ textAlign: 'left' }}>
                {ranks.type === 'team' ? 'Team' : 'Player'}
              </Table.Th>
              <Table.Th>Rating</Table.Th>
              {(full || short) && <Table.Th>Total Pts</Table.Th>}
              {full && (
                <>
                  <Table.Th>KPI Pts</Table.Th>
                  <Table.Th>League Pts</Table.Th>
                  <Table.Th>Learning XP</Table.Th>
                </>
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </div>
  )
}
